import { cn } from "@/lib/utils";
import {
  SPATIAL_AS_BUILT_ACCURACY_HEADING,
  SPATIAL_AS_BUILT_ACCURACY_PARAGRAPHS,
  SPATIAL_AS_BUILT_ASSUMPTIONS,
  SPATIAL_AS_BUILT_ASSUMPTIONS_HEADING,
  SPATIAL_AS_BUILT_CUSTOMER_LABEL,
} from "@/lib/virtualspace/spatial-as-built-disclaimer";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"] as const;

export function SpatialAsBuiltDisclaimer({
  className,
  compact = false,
}: {
  className?: string;
  /** Tighter padding/type for dense cards (e.g. deliverable picker). */
  compact?: boolean;
}) {
  return (
    <aside
      role="note"
      aria-label={`${SPATIAL_AS_BUILT_CUSTOMER_LABEL} assumptions and accuracy disclaimer`}
      className={cn(
        "@container rounded-md border vs-border-soft bg-muted/20 text-muted-foreground",
        compact ? "px-2.5 py-2" : "px-3 py-3",
        className,
      )}
    >
      {/* Side by side once there is room. Stacked, these two blocks are
          the tallest thing on the order-wizard As-Built card, and they
          are standing terms rather than something read while choosing —
          so they should cost width, not height. Container query, because
          the same note renders in a wizard card and a full-width panel. */}
      <div
        className={cn(
          "grid @xl:grid-cols-2",
          compact ? "gap-2 @xl:gap-4" : "gap-3 @xl:gap-5",
        )}
      >
        <section className="space-y-1.5">
          <h3
            className={cn(
              "font-semibold uppercase tracking-wide text-foreground/80",
              compact ? "text-[10px]" : "text-[11px]",
            )}
          >
            {SPATIAL_AS_BUILT_ASSUMPTIONS_HEADING}
          </h3>
          <ol className={cn("space-y-1.5", compact ? "text-[10px]" : "text-[11px]")}>
            {SPATIAL_AS_BUILT_ASSUMPTIONS.map((item, index) => (
              <li key={ROMAN[index] ?? index} className="flex gap-1.5 leading-relaxed">
                <span className="shrink-0 font-semibold text-foreground/70">
                  {ROMAN[index] ?? `${index + 1}`}.
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </section>

        <section
          className={cn(
            "space-y-1.5 border-t vs-border-soft @xl:border-t-0 @xl:border-l",
            compact ? "pt-2 @xl:pt-0 @xl:pl-4" : "pt-3 @xl:pt-0 @xl:pl-5",
          )}
        >
          <h3
            className={cn(
              "font-semibold uppercase tracking-wide text-foreground/80",
              compact ? "text-[10px]" : "text-[11px]",
            )}
          >
            {SPATIAL_AS_BUILT_ACCURACY_HEADING}
          </h3>
          <div
            className={cn(
              "space-y-1.5 leading-relaxed",
              compact ? "text-[10px]" : "text-[11px]",
            )}
          >
            {SPATIAL_AS_BUILT_ACCURACY_PARAGRAPHS.map((paragraph) => (
              <p key={paragraph.slice(0, 48)}>{paragraph}</p>
            ))}
          </div>
        </section>
      </div>
    </aside>
  );
}
